/**
 * The top of the breakdown, under the source bar (breakdowns tab).
 *
 * FTC personnel (`intake`): Patient | Member | Plan details, then Coverage
 * conditions, with the intake cues — "Ask" where a plan fact is unrecorded and
 * "N to ask the carrier".
 *
 * Office users — and anyone viewing from the office's Insurance Verification
 * page (`intake` is false there: viewerRole.showsIntake) — get the result:
 * ONE Plan details section: a Patient | Member table drawn like the coverage
 * table (a row of names; Show details adds their fields), then, set in under
 * it, the plan details and coverage conditions together — only the ones that
 * have an answer.
 *
 * Every value sits on one line (ClampedText).
 */
import React, { useMemo, useState } from "react";
import { Button } from "antd";
import { DownOutlined } from "@ant-design/icons";

import { formatAddress, formatPatientDOB } from "../../shared/PatientInfoCard";
import { fmtDate } from "../print/printFormat";
import ClampedText from "./ClampedText";
import { autoFillSource, coverageConditionGroups, fromLinesLabels, planDetailFields, toAskCount } from "./sheetModel";
import "./breakdownSheet.css";

function value(v) {
  return v == null || v === "" || v === "—" ? null : String(v);
}

// `ask` marks a plan fact: unrecorded, FTC sees "Ask"; anything else shows a dash.
// `fromLines`: labels whose answer was filled automatically (the procedure
// lines, the relationship, a government program's rules — `sourceOf` names it).
function Fields({ fields, intake, ask, fromLines, sourceOf }) {
  return (
    <dl className="bs-kv">
      {fields.map((f) => {
        const asking = f.value == null && ask && intake;
        const filled = f.value != null && fromLines && fromLines.has(f.label);
        return (
          <React.Fragment key={f.label}>
            <dt>{f.label}</dt>
            <dd className={f.value == null ? (asking ? "ask" : "none") : filled ? "bs-filled" : undefined}
              title={filled ? `Filled automatically from ${sourceOf(f.label)}. Set it in Edit plan to answer it yourself.` : undefined}>
              {f.value == null ? (asking ? "Ask" : "—") : <ClampedText text={f.value} />}
            </dd>
          </React.Fragment>
        );
      })}
    </dl>
  );
}

export function memberFields(policy) {
  const p = policy || {};
  return [
    { label: "Name", value: value([p.first_name, p.last_name].filter(Boolean).join(" ")) },
    { label: "Date of birth", value: value(fmtDate(p.date_of_birth)) },
    { label: "Carrier", value: value(p.insurance_name_display || p.insurance_name || p.carrier_display_name) },
    { label: "Plan name", value: value(p.plan_name) },
    { label: "Ordinal", value: value(p.insurance_type_display || p.insurance_type) },
    { label: "Group", value: value(p.contract_number) },
    { label: "Member ID", value: value(p.member_id) },
  ];
}

export function patientFields(patient, policy) {
  const pt = patient || {};
  const p = policy || {};
  const dob = formatPatientDOB(pt);
  return [
    { label: "Name", value: value([pt.first_name, pt.last_name].filter(Boolean).join(" ")) },
    { label: "Date of birth", value: value(dob === "—" ? null : fmtDate(dob) || dob) },
    { label: "Relationship", value: value(p.relation_to_member_display || p.relation_to_member) },
    { label: "Phone", value: value(pt.phone) },
    { label: "Email", value: value(pt.email) },
    { label: "Address", value: value(formatAddress(pt)) },
  ];
}

// The office view's Patient | Member table, drawn like the coverage table: a
// shaded header row (with "Show details" at its right end), then a row with
// the two names. That button adds each person's other fields under their
// name, every one in order, a dash where it is empty (only the plan facts
// leave empty fields out). Also the top of the estimate/claim results modal
// (InsuranceRequests/RequestResultsModal) — `id` keeps two on a page apart.
export function PeopleTable({ patientRows, memberRows, id = "bs-people-table" }) {
  const [open, setOpen] = useState(false);
  const cell = (title, rows) => {
    const name = (rows.find((f) => f.label === "Name") || {}).value || null;
    return (
      <td className={name ? "cat" : "cat none"}>
        <section aria-label={title}>
          <span className="bs-people-name">{name || "—"}</span>
          {open ? <Fields fields={rows.filter((f) => f.label !== "Name")} /> : null}
        </section>
      </td>
    );
  };
  return (
    <table className="bs-ctab bs-people-tab" id={id}>
      <colgroup><col style={{ width: "50%" }} /><col style={{ width: "50%" }} /></colgroup>
      <thead>
        <tr>
          <th>Patient</th>
          <th>
            <div className="bs-people-th">
              <span>Member</span>
              {/* In the header's own lettering, purple as the action it is. */}
              <button type="button" className={`bs-people-toggle${open ? " open" : ""}`} aria-expanded={open}
                aria-controls={id} onClick={() => setOpen(!open)}>
                {open ? "Hide details" : "Show details"}
                <DownOutlined className="bs-people-chev" aria-hidden />
              </button>
            </div>
          </th>
        </tr>
      </thead>
      <tbody><tr>{cell("Patient", patientRows)}{cell("Member", memberRows)}</tr></tbody>
    </table>
  );
}

// Split fields into `n` columns, top to bottom then left to right.
function columns(fields, n) {
  const per = Math.ceil(fields.length / n) || 1;
  const out = [];
  for (let i = 0; i < fields.length; i += per) out.push(fields.slice(i, i + per));
  return out;
}

export default function PlanSummary({ policy, patient, rows, maxResetDate, intake = false, onEditPlan }) {
  const answered = useMemo(() => (rows || []).filter((r) => r && !r.data_unavailable), [rows]);
  const details = useMemo(() => planDetailFields({ policy, rows: answered, maxResetDate }),
    [policy, answered, maxResetDate]);
  const groups = useMemo(() => coverageConditionGroups({ policy, rows: answered }), [policy, answered]);
  if (!policy) return null;
  const toAsk = toAskCount(details, groups);
  const fromLines = fromLinesLabels(policy);
  const sourceOf = (label) => autoFillSource(policy, label);
  const memberAll = memberFields(policy);
  // In the FTC columns the plan name shows only when recorded: most plans have none.
  const member = memberAll.filter((f) => f.label !== "Plan name" || f.value != null);
  const patientRows = patientFields(patient, policy);
  const editButton = onEditPlan ? <Button size="small" className="bs-col-act" onClick={onEditPlan}>Edit plan</Button> : null;
  const filledKey = fromLines.size ? <span className="bs-filled-key">Dotted: filled automatically (hover for where from)</span> : null;

  if (!intake) {
    const known = details.concat(...groups).filter((f) => f.value != null);
    return (
      <section className="bs-people" aria-label="Plan details" data-testid="breakdown-plan-summary"
        data-onboarding-id="bd-policy-info">
        <div className="bs-sec-head">
          <h3 className="bs-h3">Plan details</h3>
          {filledKey}
          {editButton}
        </div>
        <PeopleTable patientRows={patientRows} memberRows={memberAll} />
        {/* The plan's own facts, set in under the table: part of Plan details,
            apart from the people. Two columns, under Patient and under Member. */}
        <div className="bs-plan-facts" data-testid="breakdown-conditions">
          {known.length ? (
            <div className="bs-cols">
              {columns(known, 2).map((col) => (
                <Fields key={col[0].label} fields={col} fromLines={fromLines} sourceOf={sourceOf} />
              ))}
            </div>
          ) : <p className="bs-plan-empty">No plan details recorded for this breakdown yet.</p>}
        </div>
      </section>
    );
  }

  return (
    <>
      <div className="bs-cols" data-testid="breakdown-plan-summary" data-onboarding-id="bd-policy-info">
        <section className="bs-col" aria-label="Patient">
          <div className="bs-col-head"><h3 className="bs-h3">Patient</h3></div>
          <Fields fields={patientRows} intake={intake} />
        </section>
        <section className="bs-col" aria-label="Member">
          <div className="bs-col-head"><h3 className="bs-h3">Member</h3></div>
          <Fields fields={member} intake={intake} />
        </section>
        <section className="bs-col" aria-label="Plan details">
          <div className="bs-col-head">
            <h3 className="bs-h3">Plan details</h3>
            {editButton}
          </div>
          <Fields fields={details} intake={intake} ask fromLines={fromLines} sourceOf={sourceOf} />
        </section>
      </div>
      <section className="bs-conds" aria-label="Coverage conditions" data-testid="breakdown-conditions">
        <div className="bs-sec-head">
          <h3 className="bs-h3">Coverage conditions</h3>
          {toAsk
            ? <span className="bs-to-ask">{`${toAsk} to ask the carrier`}</span>
            : <span className="bs-all-asked">Every plan fact answered</span>}
          {filledKey}
        </div>
        <div className="bs-cols">
          {groups.map((g) => <Fields key={g[0].label} fields={g} intake={intake} ask fromLines={fromLines} sourceOf={sourceOf} />)}
        </div>
      </section>
    </>
  );
}
